import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map, Marker, Polygon, GoogleApiWrapper } from "google-maps-react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Card, CardBody, CardTitle, Modal, ModalHeader } from "reactstrap";
import { getGpsPlotData } from "store/actions";
import Rtsp from './rtsp';

// ─── Bus SVG icons (green = ignition ON, red = ignition OFF, grey = other) ────
const makeBusIcon = (color, twinkle = false, wifiCount = 0) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="40" height="40">
  ${twinkle ? `<animate attributeName="opacity" values="1;0.2;1" dur="0.8s" repeatCount="indefinite"/>` : ""}
  ${(() => {
      if (!wifiCount) return "";
      let svg = "";
      const w = Math.min(wifiCount, 4);
      const pos = { 1: [32], 2: [18, 46], 3: [14, 32, 50], 4: [12, 25, 39, 52] }[w];
      pos.forEach(cx => {
        svg += `<path d="M ${cx - 9} 3 Q ${cx} -1 ${cx + 9} 3" stroke="#263238" fill="none" stroke-width="2" stroke-linecap="round"/>`;
        svg += `<path d="M ${cx - 5} 6.5 Q ${cx} 3.5 ${cx + 5} 6.5" stroke="#263238" fill="none" stroke-width="2" stroke-linecap="round"/>`;
        svg += `<circle cx="${cx}" cy="10" r="1.5" fill="#263238"/>`;
      });
      return svg;
    })()}
  <rect x="4" y="10" width="56" height="38" rx="6" ry="6" fill="${color}"/>
  <rect x="8" y="16" width="20" height="12" rx="2" fill="#E3F2FD"/>
  <rect x="36" y="16" width="20" height="12" rx="2" fill="#E3F2FD"/>
  <rect x="4" y="34" width="56" height="8" fill="${color === '#4CAF50' ? '#388E3C' : color === '#F44336' ? '#C62828' : color === '#FFC107' ? '#FF8F00' : '#757575'}"/>
  <circle cx="16" cy="52" r="6" fill="#263238"/>
  <circle cx="16" cy="52" r="3" fill="#90A4AE"/>
  <circle cx="48" cy="52" r="6" fill="#263238"/>
  <circle cx="48" cy="52" r="3" fill="#90A4AE"/>
  <rect x="28" y="14" width="8" height="4" rx="2" fill="#FFEB3B"/>
</svg>
`)}`;

// ─── Loading placeholder ──────────────────────────────────────────────────────
const LoadingContainer = () => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    minHeight: "650px", fontSize: "16px", color: "#888",
  }}>
    Loading Map…
  </div>
);

// ─── Style helpers (module-level — never recreated) ───────────────────────────
const badgeStyle = (bg, color) => ({
  fontSize: "11px", fontWeight: 500, color,
  background: bg, border: `1px solid ${color}33`,
  borderRadius: "4px", padding: "1px 7px",
});

const infoBadgeStyle = (bg, color) => ({
  fontSize: "11px", fontWeight: 500, color,
  background: bg, borderRadius: "4px", padding: "2px 6px",
});

// ─── Custom HTML tooltip (rendered outside <Map>, no google-maps-react InfoWindow) ──
// Keeping this as its own React.memo component means its re-renders are
// completely isolated from the Marker tree.
const MarkerTooltip = React.memo(({ device, position }) => {
  if (!device || !position) return null;
  return (
    <div style={{
      position: "absolute",
      left: position.x + 12,
      top: position.y - 10,
      zIndex: 9999,
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: "8px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      padding: "8px 10px",
      minWidth: "160px",
      pointerEvents: "none",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "6px",
        fontWeight: 700, fontSize: "13px",
        color: device.ignition ? "#2e7d32" : "#555",
      }}>
        <span style={{ fontSize: "16px" }}>{device.ignition ? "🟢" : "⚫"}</span>
        {device.name}
      </div>
      <div style={{ marginTop: "6px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
        <span style={infoBadgeStyle(device.online ? "#e8f5e9" : "#fce4ec", device.online ? "#2e7d32" : "#c62828")}>
          {device.online ? "● Online" : "● Offline"}
        </span>
        <span style={infoBadgeStyle(device.ignition ? "#e8f5e9" : "#f5f5f5", device.ignition ? "#2e7d32" : "#757575")}>
          {device.ignition ? "🔑 Running" : "🔑 Stopped"}
        </span>
        <span style={infoBadgeStyle(device.inside === 1 ? "#e8f5e9" : "#fce4ec", device.inside === 1 ? "#2e7d32" : "#c62828")}>
          {device.inside === 1 ? "📍 Inside" : "📍 Outside"}
        </span>
      </div>
      {device.cameras && device.cameras.length > 0 && (
        <div style={{ marginTop: "8px", fontSize: "11px", display: "flex", flexDirection: "column", gap: "4px" }}>
          <strong style={{ color: "#333" }}>Cameras:</strong>
          {device.cameras.map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", background: "#f5f5f5", padding: "4px 8px", borderRadius: "4px" }}>
              <span style={{ fontWeight: 600 }}>{[c.cam, c.Cam, c.name].find(n => n && typeof n === "string" && n.trim() !== "" && n !== "--") || ""} <span style={{ fontWeight: 400, color: "#666" }}>({c.ip})</span></span>
              <span style={{ color: c["ip-color"] === 1 ? "#2e7d32" : "#c62828", fontSize: "10px", paddingTop: "2px" }}>
                Sig: {c.signals} | SNR: {c.snr}
              </span>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: "10px", color: "#888", marginTop: "6px" }}>
        {device.lat.toFixed(6)}, {device.lng.toFixed(6)}
      </div>
    </div>
  );
});
MarkerTooltip.displayName = "MarkerTooltip";

// ─── Memoised Polygon list ────────────────────────────────────────────────────
// Defined OUTSIDE MapsGoogle so its identity never changes, and React.memo
// guarantees it won't re-render unless antennaPolygons/onPolygonClick change.
// google + map are injected automatically by google-maps-react's <Map> via
// React.cloneElement — we must forward them to each <Polygon> so it can
// attach to the underlying Google Maps instance.
const PolygonsList = React.memo(({ antennaPolygons, onPolygonClick, google, map }) => (
  <>
    {antennaPolygons.map((antenna, idx) =>
      antenna.coords.length >= 3 ? (
        <Polygon
          key={`antenna-poly-${antenna.name || idx}`}
          google={google}
          map={map}
          paths={antenna.coords}
          strokeColor={antenna.color}
          strokeOpacity={0.9}
          strokeWeight={2}
          fillColor={antenna.color}
          fillOpacity={0.2}
          onClick={() => onPolygonClick({ name: antenna.name, rtsp: antenna.rtsp, color: antenna.color })}
        />
      ) : null
    )}
  </>
));
PolygonsList.displayName = "PolygonsList";

// ─── Memoised Marker list ─────────────────────────────────────────────────────
// Critical: this component NEVER re-renders due to InfoWindow / tooltip state
// changes because it only receives stable, memoised props.
// google + map are injected automatically by google-maps-react's <Map> via
// React.cloneElement — we must forward them to each <Marker> so it can
// attach to the underlying Google Maps instance.
const MarkersList = React.memo(({
  gpsDevices,
  getMarkerIcon,
  currentRouter,
  onMarkerClick,
  onMarkerMouseOver,
  onMarkerMouseOut,
  google,   // injected by <Map>
  map,      // injected by <Map>
}) => (
  <>
    {gpsDevices.map((device) => (
      <Marker
        key={`gps-device-${device.name}`}
        google={google}
        map={map}
        name={device.name}
        position={{ lat: device.lat, lng: device.lng }}
        title={device.name}
        onClick={onMarkerClick(device)}
        onMouseover={onMarkerMouseOver(device)}
        onMouseout={onMarkerMouseOut}
        icon={(() => {
          let color = "grey";
          if (device.ignition && device.inside === 1) color = "green";
          else if (!device.ignition && device.inside === 1) color = "grey";
          else if (!device.ignition && device.inside === 0) color = "red";
          else if (device.ignition && device.inside === 0) color = "amber";

          let isTwinkle = currentRouter && device.name.startsWith(currentRouter);
          return getMarkerIcon(color, isTwinkle, device.cameras?.length || 0);
        })()}
      />
    ))}
  </>
));
MarkersList.displayName = "MarkersList";

// ─── Main component ───────────────────────────────────────────────────────────
const MapsGoogle = (props) => {
  const dispatch = useDispatch();

  // ── Redux selectors ──────────────────────────────────────────────────────
  const user = useSelector((state) => state.Login?.user);
  const gpsData = useSelector((state) => state.GpsPlot?.data);
  const sid = user?.sessionID;

  // ── Route name ───────────────────────────────────────────────────────────
  const currentRouter = useMemo(() => {
    const raw = props.route?.RouteDetail?.Route;
    if (!raw) return "";
    return raw.split(" ")[1]?.replaceAll(")", "").replaceAll("(", "") ?? "";
  }, [props.route]);

  // ── Map / stream state ────────────────────────────────────────────────────
  const [mapCenter, setMapCenter] = useState({ lat: 28.457812, lng: 77.027763 });
  const [routerRaw, setRouterRaw] = useState([]);
  const [isLiveStreamOpen, setIsLiveStreamOpen] = useState(false);
  const [rtsp, setRtsp] = useState("");

  // ── Tooltip state (isolated — changes here NEVER touch the Marker tree) ──
  // We keep it in a plain useState; because MarkersList is React.memo'd and
  // receives only stable props, it will NOT re-render when tooltip changes.
  const [tooltip, setTooltip] = useState({ device: null, position: null });

  // ── Map container ref (used to convert page coords → container-relative) ─
  const mapContainerRef = useRef(null);

  // ── Fetch on mount & every 1 minute ────────────────────────────────────────
  useEffect(() => {
    if (sid) {
      dispatch(getGpsPlotData(sid));
      const intervalId = setInterval(() => {
        dispatch(getGpsPlotData(sid));
      }, 60000); // 1 minute
      return () => clearInterval(intervalId);
    }
  }, [sid]);

  // ── Parse raw API data ────────────────────────────────────────────────────
  useEffect(() => {
    if (gpsData) {
      setRouterRaw(gpsData?.OK?.Router[0] || gpsData?.OK?.router || []);
    }
  }, [gpsData]);



  // ── Derived data (stable references) ─────────────────────────────────────
  const antennaPolygons = useMemo(() => {
    if (!routerRaw?.length) return [];
    return routerRaw.map((router) => {
      const coords = [];
      for (let i = 1; i <= 3; i++) {
        const lat = parseFloat(router[`lat${i}`]);
        const lng = parseFloat(router[`long${i}`]);
        if (!isNaN(lat) && !isNaN(lng)) coords.push({ lat, lng });
        else break;
      }
      const color = router["Status"] === "Online" ? "Green"
        : router["Status"] === "Offline" ? "Red"
          : "grey";
      return { name: router.Name, coords, color, rtsp: router.cam_live };
    });
  }, [routerRaw]);

  const gpsDevices = useMemo(() => {
    if (!routerRaw?.length) return [];
    const deviceMap = {};

    // 1. Collect from Cam arrays (has inside + cameras info)
    routerRaw.forEach((d) => {
      (d["Cam"] || []).forEach((e) => {
        if (!e.name) return;
        const lat = parseFloat(e.lat);
        const lng = parseFloat(e.long);
        if (isNaN(lat) || isNaN(lng)) return;
        if (!deviceMap[e.name]) {
          deviceMap[e.name] = {
            name: e.name,
            online: String(e.online) === "1",
            lat,
            lng,
            ignition: String(e.ignition) === "true",
            inside: parseInt(e.inside, 10),
            cameras: e.cameras || [],
          };
        }
      });
    });

    // 2. Add gps-only devices not already in Cam
    routerRaw.forEach((d) => {
      (d["gps"] || []).forEach((e) => {
        if (!e.device) return;
        if (deviceMap[e.device]) return;
        const lat = parseFloat(e.lat);
        const lng = parseFloat(e.long);
        if (isNaN(lat) || isNaN(lng)) return;
        deviceMap[e.device] = {
          name: e.device,
          online: String(e.online) === "1",
          lat,
          lng,
          ignition: String(e.ignition) === "true",
          inside: 0,
          cameras: [],
        };
      });
    });

    return Object.values(deviceMap)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [routerRaw]);

  // ── Auto-update Tooltip if its data updates ───────────────────────────────
  useEffect(() => {
    setTooltip((prev) => {
      if (!prev.device) return prev;
      const latestDevice = gpsDevices.find((d) => d.name === prev.device.name);
      if (latestDevice && latestDevice !== prev.device) {
        return { ...prev, device: latestDevice };
      }
      return prev;
    });
  }, [gpsDevices]);

  // ── Stable icon objects (Size/Point created once) ─────────────────────────
  const iconCache = useRef({});
  const getMarkerIcon = useCallback((colorName, twinkle, wifiCount) => {
    if (!props.google) return null;
    const key = `${colorName}-${twinkle}-${wifiCount}`;
    if (!iconCache.current[key]) {
      const hex = {
        green: "#4CAF50",
        red: "#F44336",
        grey: "#9E9E9E",
        amber: "#FFC107"
      }[colorName];
      iconCache.current[key] = {
        url: makeBusIcon(hex, twinkle, wifiCount),
        scaledSize: new props.google.maps.Size(40, 40),
        anchor: new props.google.maps.Point(20, 40),
      };
    }
    return iconCache.current[key];
  }, [props.google]);

  // ── Auto-centre ───────────────────────────────────────────────────────────
  useEffect(() => {
    let leftmostPoint = null;
    if (antennaPolygons.length > 0) {
      antennaPolygons.forEach((antenna) => {
        antenna.coords.forEach((coord) => {
          if (!leftmostPoint || coord.lng < leftmostPoint.lng) {
            leftmostPoint = coord;
          }
        });
      });
    }

    if (leftmostPoint) {
      setMapCenter({ lat: leftmostPoint.lat, lng: leftmostPoint.lng + 0.00030 });
    }
  }, [gpsDevices, antennaPolygons, gpsData]);

  // ── Stable event handlers ─────────────────────────────────────────────────
  const onMapClicked = useCallback(() => {
    setTooltip({ device: null, position: null });
  }, []);

  // Converts a google.maps.Marker DOM position to container-relative coords
  const getRelativePosition = useCallback((marker) => {
    if (!mapContainerRef.current || !marker) return null;
    try {
      const rect = mapContainerRef.current.getBoundingClientRect();
      const point = marker.getPosition();
      // Use the map's projection to get pixel coords
      const overlay = new props.google.maps.OverlayView();
      overlay.setMap(marker.getMap());
      // Fall back to mouse event coords captured via nativeEvent
      return null; // Will be set via the e.domEvent path below
    } catch {
      return null;
    }
  }, [props.google]);

  // onMouseover: receives (markerProps, marker, e) from google-maps-react
  const onMarkerMouseOver = useCallback(
    (deviceObj) => (markerProps, marker, e) => {
      let position = null;
      try {
        const rect = mapContainerRef.current?.getBoundingClientRect();
        const domEvent = e?.domEvent || e;
        if (rect && domEvent?.clientX != null) {
          position = {
            x: domEvent.clientX - rect.left,
            y: domEvent.clientY - rect.top,
          };
        }
      } catch (_) { /* ignore */ }
      setTooltip({ device: deviceObj, position });
    },
    []
  );

  const onMarkerMouseOut = useCallback(() => {
    setTooltip({ device: null, position: null });
  }, []);

  const onMarkerClick = useCallback(
    (deviceObj) => (markerProps, marker, e) => {
      let position = null;
      try {
        const rect = mapContainerRef.current?.getBoundingClientRect();
        const domEvent = e?.domEvent || e;
        if (rect && domEvent?.clientX != null) {
          position = {
            x: domEvent.clientX - rect.left,
            y: domEvent.clientY - rect.top,
          };
        }
      } catch (_) { /* ignore */ }
      setTooltip((prev) =>
        prev.device?.name === deviceObj.name && prev.position
          ? { device: null, position: null }  // toggle off on second click
          : { device: deviceObj, position }
      );
    },
    []
  );

  const onPolygonClick = useCallback((deviceObj) => {
    if (deviceObj.color === "Green") {
      setIsLiveStreamOpen((prev) => !prev);
      setRtsp(deviceObj.rtsp);
    }
  }, []);

  // ── Summary counts ────────────────────────────────────────────────────────
  const onlineCount = useMemo(() => gpsDevices.filter((d) => d.online === true).length, [gpsDevices]);
  const ignitionCount = useMemo(() => gpsDevices.filter((d) => d.ignition === true).length, [gpsDevices]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Card>
        <CardBody style={{ minHeight: "780px", position: "relative" }}>

          {/* ── Card Header ─────────────────────────────────────────────── */}
          <CardTitle className="mb-2 d-flex align-items-center flex-wrap gap-2">
            <span>
              <i className="mdi mdi-map-marker-radius text-primary me-1" />
              Map
            </span>
            {gpsData && (
              <>
                <span style={badgeStyle("#e3f2fd", "#1565C0")}>
                  {antennaPolygons.length} Antenna{antennaPolygons.length !== 1 ? "s" : ""}
                </span>
                <span style={badgeStyle("#e8f5e9", "#2e7d32")}>
                  {onlineCount}/{gpsDevices.length} Online
                </span>
                <span style={badgeStyle("#fff8e1", "#f57f17")}>
                  {ignitionCount} Running
                </span>
              </>
            )}
          </CardTitle>

          {/* ── Map container ───────────────────────────────────────────── */}
          <div
            ref={mapContainerRef}
            id="gmaps-markers"
            className="gmaps"
            style={{ position: "relative", minHeight: "700px", width: "100%" }}
          >
            <Map
              google={props.google}
              style={{ width: "100%", height: "100%" }}
              zoom={20}
              center={mapCenter}
              initialCenter={mapCenter}
              onClick={onMapClicked}
            >
              {/* Stable memoised children – never re-render on tooltip state change */}
              <PolygonsList
                antennaPolygons={antennaPolygons}
                onPolygonClick={onPolygonClick}
              />
              <MarkersList
                gpsDevices={gpsDevices}
                getMarkerIcon={getMarkerIcon}
                currentRouter={currentRouter}
                onMarkerClick={onMarkerClick}
                onMarkerMouseOver={onMarkerMouseOver}
                onMarkerMouseOut={onMarkerMouseOut}
              />
            </Map>

            {/* Custom HTML tooltip – rendered OUTSIDE <Map> so it never
                causes markers to re-render */}
            <MarkerTooltip device={tooltip.device} position={tooltip.position} />
          </div>

          {/* ── Legend ─────────────────────────────────────────────────── */}
          {(antennaPolygons.length > 0 || gpsDevices.length > 0) && (
            <div style={{
              display: "flex", flexWrap: "wrap", gap: "10px",
              marginTop: "10px", padding: "6px 2px",
              borderTop: "1px solid #f0f0f0",
            }} />
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={isLiveStreamOpen}
        style={{ maxWidth: "730px" }}
        toggle={() => setIsLiveStreamOpen((p) => !p)}
      >
        <ModalHeader toggle={() => setIsLiveStreamOpen((p) => !p)}>
          Live Stream
        </ModalHeader>
        <Rtsp rtsp={rtsp} />
      </Modal>
    </>
  );
};

// ─── PropTypes ────────────────────────────────────────────────────────────────
MapsGoogle.propTypes = {
  google: PropTypes.object,
};

export default connect(
  null,
  {}
)(
  GoogleApiWrapper({
    apiKey: "AIzaSyBIrSlKJUwF2aSArBQh4NoNOVa0YuKDnDg",
    LoadingContainer: LoadingContainer,
    v: "3",
  })(MapsGoogle)
);
